import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    useMediaQuery,
    Stack,
    useTheme,
    Card,
    CardContent,
    InputBase,
    IconButton,
    Button,
    Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { auditLogService, UserActionEntry } from '../../services/auditLogService';
import UserActionTableSkeleton from './components/UserActionTableSkeleton';
import UserActionCardSkeleton from './components/UserActionCardSkeleton';
import { DataTable } from '../../components/DataTable';

const UserAction = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // State management
    const [logs, setLogs] = useState<UserActionEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [resultsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    // Fetch logs from API
    const fetchLogs = useCallback(async (page: number = 1) => {
        try {
            setLoading(true);
            setError(null);
            
            const params: any = {
                page,
                limit: resultsPerPage,
            };
            
            // Add account number filter if query exists
            if (searchQuery.trim()) {
                params.account_number = searchQuery.trim();
            }
            
            // Add date filters if provided
            if (startDate) {
                params.after_date = startDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
            }
            
            if (endDate) {
                params.before_date = endDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
            }
            
            const response = await auditLogService.getUserActions(params);

            setLogs(response.data);
            setTotalResults(response.total);
            setCurrentPage(response.page);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch user actions');
            console.error('Error fetching user actions:', err);
        } finally {
            setLoading(false);
        }
    }, [resultsPerPage, searchQuery, startDate, endDate]);

    // Load logs on component mount
    useEffect(() => {
        fetchLogs(1);
    }, [fetchLogs]);

    // Handle search
    const handleSearch = () => {
        setCurrentPage(1);
        fetchLogs(1);
    };

    // Handle search on Enter key
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Handle date changes
    const handleStartDateChange = (date: Date | null) => {
        setStartDate(date);
        setCurrentPage(1);
        fetchLogs(1);
    };

    const handleEndDateChange = (date: Date | null) => {
        setEndDate(date);
        setCurrentPage(1);
        fetchLogs(1);
    };

    // Clear all filters
    const clearFilters = () => {
        setSearchQuery('');
        setStartDate(null);
        setEndDate(null);
        setCurrentPage(1);
        fetchLogs(1);
    };

    // Show error message if there's an error
    if (error) {
        return (
            <Box px={isMobile ? 0 : 2} py={isMobile ? 1 : 2} display="flex" justifyContent="center" alignItems="center">
                <Typography color="error">
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <Box px={isMobile?0:2} py={ isMobile?1:2}>
            <Box
                component={Paper}
                elevation={0}
                sx={{
                    borderRadius: '12px',
                    p: isMobile?2:4,
                    backgroundColor: '#fff',
                    boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.05)',
                }}
            >
                {/* Header */}
                <Typography fontSize="22px" fontWeight={600} color='#132F5F' mb={3}>
                    User Action
                </Typography>

                {/* Filters Section */}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Stack
                        direction={isMobile ? 'column' : 'row'}
                        spacing={isMobile ? 2 : 3}
                        mb={3}
                        sx={{ width: '100%' }}
                    >
                        {/* Account Number Search */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: { xs: '24px', sm: '28px', md: '32px' },
                                backgroundColor: '#EEF2FF',
                                px: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
                                py: { xs: 1, sm: 1.25, md: 1.5, lg: 2 },
                                width: isMobile ? '95%' : '25%',
                                flexShrink: 0,
                            }}
                        >
                            <InputBase
                                placeholder="Search by account number"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={handleKeyPress}
                                sx={{
                                    flex: 1,
                                    fontSize: { xs: '12px', sm: '13px', md: '14px', lg: '15px' },
                                    fontFamily: 'Poppins, sans-serif',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    '& input': {
                                        padding: { xs: '8px 0', sm: '10px 0', md: '12px 0' },
                                    },
                                }}
                            />

                            <IconButton
                                sx={{
                                    backgroundColor: '#132F5F',
                                    color: '#fff',
                                    ml: { xs: 0.5, sm: 1, md: 1.5, lg: 2 },
                                    width: { xs: 40, sm: 44, md: 48, lg: 52 },
                                    height: { xs: 40, sm: 44, md: 48, lg: 52 },
                                    flexShrink: 0,
                                    '&:hover': {
                                        backgroundColor: '#0f254d',
                                    },
                                }}
                                onClick={handleSearch}
                            >
                                <SearchIcon sx={{ fontSize: { xs: '18px', sm: '20px', md: '22px', lg: '24px' } }} />
                            </IconButton>
                        </Box>

                        {/* Date Filters */}
                        <Stack
                            direction={isMobile ? 'column' : 'row'}
                            spacing={isMobile ? 2 : 2}
                            sx={{ 
                                width: isMobile ? '100%' : '50%',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}
                        >
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={handleStartDateChange}
                                slotProps={{
                                    textField: {
                                        size: isMobile ? 'medium' : 'small',
                                        fullWidth: isMobile,
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: isMobile ? '16px' : '12px',
                                                backgroundColor: '#fff',
                                                height: isMobile ? '56px' : 'auto',
                                                fontSize: isMobile ? '16px' : '14px',
                                                '& fieldset': {
                                                    borderColor: '#E5E7EB',
                                                    borderWidth: isMobile ? '2px' : '1px',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#D1D5DB',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#132F5F',
                                                    borderWidth: isMobile ? '2px' : '1px',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: isMobile ? '16px' : '14px',
                                                color: '#6B7280',
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: isMobile ? '16px' : '14px',
                                                padding: isMobile ? '16px 14px' : '8px 14px',
                                            },
                                        },
                                    },
                                }}
                            />
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={handleEndDateChange}
                                slotProps={{
                                    textField: {
                                        size: isMobile ? 'medium' : 'small',
                                        fullWidth: isMobile,
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: isMobile ? '16px' : '12px',
                                                backgroundColor: '#fff',
                                                height: isMobile ? '56px' : 'auto',
                                                fontSize: isMobile ? '16px' : '14px',
                                                '& fieldset': {
                                                    borderColor: '#E5E7EB',
                                                    borderWidth: isMobile ? '2px' : '1px',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#D1D5DB',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#132F5F',
                                                    borderWidth: isMobile ? '2px' : '1px',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: isMobile ? '16px' : '14px',
                                                color: '#6B7280',
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: isMobile ? '16px' : '14px',
                                                padding: isMobile ? '16px 14px' : '8px 14px',
                                            },
                                        },
                                    },
                                }}
                            />
                        </Stack>

                        {/* Clear Filters Button */}
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                width: isMobile ? '100%' : '25%',
                                justifyContent: isMobile ? 'center' : 'flex-end'
                            }}
                        >
                            <Button
                                variant='contained'
                                onClick={clearFilters}
                                sx={{
                                    backgroundColor: '#132F5F',
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    width: { xs: '100%', sm: '120px', md: '130px', lg: '140px' },
                                    fontSize: { xs: '12px', sm: '13px', md: '14px', lg: '15px' },
                                    lineHeight: '24px',
                                    fontWeight: 500,
                                    px: { xs: 2, sm: 2.5, md: 3 },
                                    py: { xs: 1, sm: 1.25, md: 1.5 },
                                    height: { xs: 48, sm: 50, md: 52 },
                                    fontFamily: 'Poppins, sans-serif',
                                    '&:hover': {
                                        backgroundColor: '#0f254d',
                                    },
                                }}
                            >
                                Clear Filters
                            </Button>
                        </Box>
                    </Stack>
                </LocalizationProvider>

                {loading ? (
                    isMobile ? (
                        <Stack spacing={2}>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <UserActionCardSkeleton key={index} />
                            ))}
                        </Stack>
                    ) : (
                        <UserActionTableSkeleton />
                    )
                ) : logs.length === 0 ? (
                    <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
                        <Typography 
                            variant='h6' 
                            color='text.secondary'
                            sx={{ 
                                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                                textAlign: 'center',
                                px: { xs: 2, sm: 0 }
                            }}
                        >
                            No user actions found
                        </Typography>
                    </Box>
                ) : isMobile ? (
                    <>
                        {/* MOBILE: Card View */}
                        <Stack spacing={2}>
                            {logs.map((log) => (
                                <Card
                                    key={log.log_id}
                                    sx={{
                                        backgroundColor: '#FAF9F9',
                                        borderRadius: '16px',
                                        boxShadow: '0px 4px 12px rgba(0,0,0,0.05)',
                                        border: '2px solid #E0E0E0'
                                    }}
                                >
                                    <CardContent>
                                        <Stack spacing={1}>
                                            <Typography fontWeight={600} fontSize='14px'>
                                                Action: <Typography component='span'>{auditLogService.formatAction(log.action)}</Typography>
                                            </Typography>
                                            <Typography fontSize='14px'>
                                                <strong>Account Number:</strong> {log.account_number}
                                            </Typography>
                                            <Typography fontSize='14px'>
                                                <strong>SA ID:</strong> {log.sa_id}
                                            </Typography>
                                            <Typography fontSize='14px'>
                                                <strong>User:</strong> {log.user}
                                            </Typography>
                                            <Typography fontSize='14px'>
                                                <strong>Timestamp:</strong> {auditLogService.formatTimestamp(log.timestamp)}
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                        
                        {/* Mobile Pagination */}
                        {!loading && totalResults > 0 && (
                            <Box 
                                mt={3} 
                                display='flex' 
                                flexDirection='column'
                                justifyContent='space-between' 
                                alignItems='stretch'
                                gap={2}
                                sx={{
                                    backgroundColor: '#fff',
                                    padding: '12px 16px',
                                    borderRadius: '12px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                }}
                            >
                                <Typography 
                                    fontSize='12px' 
                                    color='#49454F'
                                    textAlign='center'
                                >
                                    Showing <b>{((currentPage - 1) * resultsPerPage) + 1}</b> to <b>{Math.min(currentPage * resultsPerPage, totalResults)}</b> of <b>{totalResults}</b> results
                                </Typography>
                                <Box 
                                    display='flex' 
                                    justifyContent='center'
                                    width='100%'
                                >
                                    <Pagination 
                                        count={Math.ceil(totalResults / resultsPerPage)} 
                                        page={currentPage} 
                                        onChange={(event, page) => {
                                            setCurrentPage(page);
                                            fetchLogs(page);
                                        }}
                                        size='small'
                                        siblingCount={0}
                                        boundaryCount={1}
                                        showFirstButton={false}
                                        showLastButton={false}
                                        sx={{
                                            '& .MuiPaginationItem-root': {
                                                color: '#666',
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                minWidth: '32px',
                                                height: '32px'
                                            },
                                            '& .Mui-selected': {
                                                backgroundColor: '#203A6B',
                                                color: '#fff',
                                                '&:hover': {
                                                    backgroundColor: '#1a2e56'
                                                }
                                            },
                                            '& .MuiPaginationItem-ellipsis': {
                                                fontSize: '12px'
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}
                    </>
                ) : (
                    // DESKTOP: DataTable View
                    <DataTable
                        columns={[
                            { 
                                key: 'action', 
                                label: 'Action', 
                                width: '120px',
                                render: (value, row) => auditLogService.formatAction(row.action)
                            },
                            { 
                                key: 'account_number', 
                                label: 'Account Number', 
                                width: '150px',
                                render: (value) => value || '—'
                            },
                            { 
                                key: 'sa_id', 
                                label: 'SA ID', 
                                width: '120px',
                                render: (value) => value || '—'
                            },
                            { 
                                key: 'user', 
                                label: 'User', 
                                width: '120px',
                                render: (value) => (
                                    <Box sx={{ 
                                        textAlign: value ? 'left' : 'center'
                                    }}>
                                        {value || '—'}
                                    </Box>
                                )
                            },
                            { 
                                key: 'timestamp', 
                                label: 'Timestamp', 
                                width: '150px',
                                render: (value, row) => auditLogService.formatTimestamp(row.timestamp)
                            },
                        ]}
                        data={logs}
                        loading={loading}
                        total={totalResults}
                        currentPage={currentPage}
                        limit={resultsPerPage}
                        onPageChange={(page) => {
                            setCurrentPage(page);
                            fetchLogs(page);
                        }}
                        emptyMessage="No user actions found"
                    />
                )}
            </Box>
        </Box>
    );
};

export default UserAction;
